import React from 'react';
import { TrackedLink } from 'components/common/TrackedLink';
import { useWorkspacePath } from 'pages/workspaces/useWorkspace';
import { basePaths } from 'app-constants';
import { useQueryAndHash } from './useQueryAndHash';
import { LinkHelperProps } from './index';

type CollectionType = 'other' | 'packages' | undefined;

interface CollectionLinkProps extends LinkHelperProps {
  slug?: string;
  type?: CollectionType;
}

export type { CollectionLinkProps };

export function parseCollectionPath(path: string) {
  // Parse collection paths like /learning/collections/{id}
  const collectionPathRegex = new RegExp(`^${basePaths.collections}/([^/]+)$`);
  const match = path.match(collectionPathRegex);
  if (match) {
    return {
      slug: match[1],
    };
  }
  return null;
}

export function useCollectionPath(
  slug?: string,
  type?: CollectionType,
  query?: string | null,
  hash?: string | null,
) {
  const workspacePath = useWorkspacePath();

  const path = React.useMemo(() => {
    const parts = [basePaths.collections];

    if (type) {
      parts.push(type);
    }

    if (slug) {
      parts.push(slug);
    }

    return workspacePath + parts.join('/').replace(/\/+/g, '/');
  }, [slug, type, workspacePath]);

  return useQueryAndHash(path, query, hash);
}

export function CollectionLink({
  to,
  slug,
  query,
  hash,
  type = 'other',
  ...props
}: CollectionLinkProps) {
  const path = useCollectionPath(slug, type, query, hash);
  return <TrackedLink to={to ? to : path} {...props} />;
}

// Convenience components for specific collection types
export function PackageLink(props: Omit<CollectionLinkProps, 'type'>) {
  return <CollectionLink type="packages" {...props} />;
}
